import axios from "axios";

import { baseUrl } from "./baseUrl";
import { uploadImage, updateImage } from './imageApiV2';

const api = axios.create({
    baseURL: `${baseUrl}/admin/blog`,
    headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
    },
});

export const getBlogs = async (userType) => {
    try {
        const res = await api.post("/blog-list", { userType });
        return res.data.data;
    } catch (error) {
        console.error("Error fetching dashboard data:", error);
        throw error;
    }
};

export const createBlog = async (data, coverImage, featuredImages, galleryImages) => {
    try {
        // Upload cover image to Image API V2 if provided
        if (coverImage && coverImage instanceof File) {
            console.log('📤 Uploading blog cover image to Image API V2...');
            const imageResult = await uploadImage(coverImage, 'blogs', false);

            data.fileId = imageResult.imageId;
            data.fileVersion = imageResult.version;
            data.file = imageResult.url;

            console.log('✅ Blog cover image uploaded:', imageResult);
        }

        // Upload featured images (array) to Image API V2 if provided
        const existingFeaturedUrls = data.featuredImages || [];
        if (featuredImages && featuredImages.length > 0) {
            console.log('📤 Uploading blog featured images to Image API V2...');
            const uploadedFeatured = await Promise.all(
                featuredImages.filter(img => img instanceof File).map(img => uploadImage(img, 'blogs', false))
            );
            data.featuredImages = [...existingFeaturedUrls, ...uploadedFeatured.map(r => r.url)];
            data.featuredImageIds = uploadedFeatured.map(r => r.imageId);
            console.log('✅ Blog featured images uploaded:', uploadedFeatured.length);
        } else {
            data.featuredImages = existingFeaturedUrls;
        }

        // Upload gallery images (array) to Image API V2 if provided
        const existingGalleryUrls = data.imageGallery || [];
        if (galleryImages && galleryImages.length > 0) {
            console.log('📤 Uploading blog gallery images to Image API V2...');
            const uploadedGallery = await Promise.all(
                galleryImages.filter(img => img instanceof File).map(img => uploadImage(img, 'blogs', false))
            );
            data.imageGallery = [...existingGalleryUrls, ...uploadedGallery.map(r => r.url)];
            data.galleryImageIds = uploadedGallery.map(r => r.imageId);
            console.log('✅ Blog gallery images uploaded:', uploadedGallery.length);
        } else {
            data.imageGallery = existingGalleryUrls;
        }

        const res = await api.post("/create", data);
        return res.data;
    } catch (error) {
        console.error("Error creating blog:", error);
        throw error;
    }
}

export const editBlog = async (id, data, coverImage, featuredImages, galleryImages) => {
    try {
        // Upload cover image to Image API V2 if provided
        if (coverImage && coverImage instanceof File) {
            console.log('📤 Uploading blog cover image to Image API V2...');

            // Use updateImage if fileId exists, otherwise upload new
            let imageResult;
            if (data.fileId) {
                imageResult = await updateImage(data.fileId, coverImage);
                console.log('✅ Blog cover image updated to version:', imageResult.version);
            } else {
                imageResult = await uploadImage(coverImage, 'blogs', false);
                console.log('✅ Blog cover image uploaded:', imageResult);
            }

            data.fileId = imageResult.imageId;
            data.fileVersion = imageResult.version;
            data.file = imageResult.url;
        }

        // Upload featured images (array) to Image API V2 if provided
        const existingFeaturedUrls = data.featuredImages || [];
        if (featuredImages && featuredImages.length > 0) {
            console.log('📤 Uploading blog featured images to Image API V2...');
            const uploadedFeatured = await Promise.all(
                featuredImages.filter(img => img instanceof File).map(img => uploadImage(img, 'blogs', false))
            );
            data.featuredImages = [...existingFeaturedUrls, ...uploadedFeatured.map(r => r.url)];
            data.featuredImageIds = uploadedFeatured.map(r => r.imageId);
            console.log('✅ Blog featured images uploaded:', uploadedFeatured.length);
        } else {
            data.featuredImages = existingFeaturedUrls;
        }

        // Upload gallery images (array) to Image API V2 if provided
        const existingGalleryUrls = data.imageGallery || [];
        if (galleryImages && galleryImages.length > 0) {
            console.log('📤 Uploading blog gallery images to Image API V2...');
            const uploadedGallery = await Promise.all(
                galleryImages.filter(img => img instanceof File).map(img => uploadImage(img, 'blogs', false))
            );
            data.imageGallery = [...existingGalleryUrls, ...uploadedGallery.map(r => r.url)];
            data.galleryImageIds = uploadedGallery.map(r => r.imageId);
            console.log('✅ Blog gallery images uploaded:', uploadedGallery.length);
        } else {
            data.imageGallery = existingGalleryUrls;
        }

        const res = await api.post(`/edit/${id}`, data);
        return res.data;
    } catch (error) {
        console.error("Error updating blog:", error);
        throw error;
    }
}

export const getBlogById = async (id) => {
    try {
        const res = await api.post(`/${id}`);
        return res.data.blog;
    } catch (error) {
        console.error("Error fetching blog by ID:", error);
        throw error;
    }
}

export const updateBlogStatus = async (data) => {
    try {
        const res = await api.post(`/edit/status`, { data });
        return res.data;
    } catch (error) {
        console.error("Error updating blog status:", error);
        throw error;
    }
}

export const acceptOrRejectBlog = async (data) => {
    try {
        const res = await api.post(`/blog/accept-reject`, { data });
        return res.data.data;
    } catch (error) {
        console.error("Error updating blog status:", error);
        throw error;
    }
}


export const deleteBlog = async (id) => {
    try {
        const res = await api.delete(`/delete/${id}`);
        return res.data;
    } catch (error) {
        console.error("Error deleting blog:", error);
        throw error;
    }
}